import { fmtDate } from '../../lib/format';



export const CORES = [2, 4, 6, 8, 10, 12, 16, 20, 24, 32];
export const MEMORY = [2, 4, 8];

export const PORT_PRESET = [3389, 5900, 5901, 5902, 5903, 5931, 5938, 5939, 5944, 5950, 6039, 6040, 7070, 63333];
export const TIMEZONES = [
  'Europe/Moscow', 'Europe/Kiev', 'Europe/Minsk', 'Europe/Berlin', 'Europe/London', 'Europe/Paris', 'Europe/Warsaw',
  'Europe/Istanbul', 'Asia/Almaty', 'Asia/Tashkent', 'Asia/Yekaterinburg', 'Asia/Novosibirsk', 'Asia/Dubai',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC',
];
export type ChipItem = [value: string, label: string];
export const PHASES: ChipItem[] = [
  ['new', 'новые'],
  ['warming', 'греются'],
  ['ready', 'готовы'],
  ['targeting', 'в задачах'],
  ['retired', 'списаны'],
];
export const STATUSES: ChipItem[] = [
  ['created', 'созданы'],
  ['ready', 'ждут'],
  ['failed', 'с ошибкой'],
  ['completed', 'завершены'],
  ['idle', 'простаивают'],
];
export const ENGINES: ChipItem[] = [
  ['bing', 'Bing'],
  ['google', 'Google'],
  ['none', 'не назначен'],
];
export const PLATFORMS: ChipItem[] = [
  ['android', 'Android'],
  ['windows', 'Windows'],
  ['macos', 'macOS'],
  ['linux', 'Linux'],
];
export const PLATFORM_LABEL: Record<string, string> = {
  android: 'Android',
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
};
export const FP_SITES = ['creepjs', 'browserscan', 'pixelscan', 'clearcote-audit', 'iphey'] as const;



export type NodeCluster = {
  id: string;
  name: string;
  os?: string;
  region?: string;
  enabled?: boolean;
  hostname?: string;
  sshHost?: string;
  status: string;
  deployStageLabel?: string;
  deployProgress?: number;
  live?: {
    workMode?: string;
    profilesTotal?: number;
    profilesActive?: number;
    profilesIdle?: number;
    profilesReady?: number;
    profilesDiskMb?: number | null;
    profilesDiskTotalMb?: number | null;
  };
};

export type ClusterProfileStats = {
  total?: number;
  active?: number;
  idle?: number;
  ready?: number;
  diskMb?: number | null;
  diskTotalMb?: number | null;
};

export type ProfileListItem = {
  id: string;
  status: string;
  phase: string;
  farmDays?: number;
  runCount?: number;
  createdAt?: string;
};

export type Donor = {
  id: string;
  name: string;
  platform: string;
  gpu?: string;
  cores?: number;
  memory?: number;
  screen?: string;
  addedAt?: string;
};

export type TemplateMode = 'native' | 'fixed' | 'random';
export type AutoFixed = 'auto' | 'fixed';


export type ProfileTemplate = {
  platform: string;
  brand: string;
  brandVersion: { mode: string; values: string[] };
  timezone: { mode: string; value: string };
  language: { mode: string; value: string };
  noise: boolean;
  canvas: string;
  webgl: string;
  hardware: { source: string; cores: number[]; memory: number[] };
  webrtc: string;
  blockedPorts: number[];
  updatedAt?: string;
};

export type TemplateBody = Omit<ProfileTemplate, 'updatedAt'>;

export const DEFAULT_TEMPLATE: TemplateBody = {
  platform: 'android',
  brand: 'Chrome',
  brandVersion: { mode: 'native', values: [] },
  timezone: { mode: 'auto', value: '' },
  language: { mode: 'auto', value: '' },
  noise: true,
  canvas: 'auto',
  webgl: 'auto',
  hardware: { source: 'seed', cores: [], memory: [] },
  webrtc: 'proxy',
  blockedPorts: [],
};


export type TemplateForm = Omit<TemplateBody, 'brandVersion'> & {
  brandVersion: { mode: string; text: string };
};

export function parseVersions(text: string): string[] {
  return text.split(/[\s,;]+/).filter(Boolean);
}

export function toForm(t: TemplateBody): TemplateForm {
  return {
    ...t,
    brandVersion: { mode: t.brandVersion.mode, text: (t.brandVersion.values || []).join(', ') },
    timezone: { mode: t.timezone.mode, value: t.timezone.value || '' },
    language: { mode: t.language.mode, value: t.language.value || '' },
    noise: t.noise !== false,
    hardware: { source: t.hardware.source, cores: [...(t.hardware.cores || [])], memory: [...(t.hardware.memory || [])] },
    blockedPorts: [...(t.blockedPorts || [])],
  };
}

export function fromForm(f: TemplateForm): TemplateBody {
  return {
    platform: f.platform,
    brand: f.brand,

    brandVersion: { mode: f.brand === 'Edge' ? 'native' : f.brandVersion.mode, values: parseVersions(f.brandVersion.text) },
    timezone: { mode: f.timezone.mode, value: f.timezone.value.trim() },
    language: { mode: f.language.mode, value: f.language.value.trim() },
    noise: f.noise,
    canvas: f.canvas,
    webgl: f.webgl,
    hardware: { source: f.hardware.source, cores: [...f.hardware.cores], memory: [...f.hardware.memory] },
    webrtc: f.webrtc,
    blockedPorts: [...f.blockedPorts],
  };
}


export function canonical(t: TemplateBody): string {
  return JSON.stringify({
    platform: t.platform,
    brand: t.brand,
    brandVersion: {
      mode: t.brandVersion.mode,
      values: t.brandVersion.mode === 'native' ? [] : t.brandVersion.values,
    },
    timezone: { mode: t.timezone.mode, value: t.timezone.mode === 'fixed' ? t.timezone.value : '' },
    language: { mode: t.language.mode, value: t.language.mode === 'fixed' ? t.language.value : '' },
    noise: t.noise,
    canvas: t.canvas,
    webgl: t.webgl,
    hardware: { source: t.hardware.source, cores: t.hardware.cores, memory: t.hardware.memory },
    webrtc: t.webrtc,
    blockedPorts: t.blockedPorts,
  });
}

export function addPorts(current: number[], list: number[]): number[] {
  const next = new Set(current);
  for (const p of list) if (Number.isInteger(p) && p >= 1 && p <= 65535) next.add(p);
  return [...next].sort((a, b) => a - b).slice(0, 64);
}

export function previewRows(f: TemplateBody, engineChromium: number): Array<[string, string]> {
  const osMap: Record<string, string> = {
    random: 'случайная по весам',
    android: 'Android (Mobile)',
    windows: 'Windows',
    macos: 'macOS',
    linux: 'Linux',
  };
  const os = osMap[f.platform] || f.platform;
  const ver =
    f.brandVersion.mode === 'native'
      ? `${engineChromium || 'движка'} (как у движка)`
      : f.brandVersion.mode === 'fixed'
        ? f.brandVersion.values[0] || '—'
        : `одна из: ${f.brandVersion.values.join(', ') || '—'}`;
  const hw =
    f.platform === 'android'
      ? 'мобильное по seed (Pixel / Galaxy)'
      : f.hardware.source === 'library'
        ? 'реальный донор из библиотеки'
        : `${f.hardware.cores.length ? `CPU ${f.hardware.cores.join('/')}` : 'CPU по seed'}, ${
            f.hardware.memory.length ? `RAM ${f.hardware.memory.join('/')} ГБ` : 'RAM по seed'
          }`;
  const webrtc: Record<string, string> = {
    proxy: 'IP прокси',
    disable_udp: 'non-proxied UDP выключен',
    default: 'по умолчанию браузера',
  };
  return [
    ['Браузер', `${f.brand === 'Edge' ? 'Microsoft Edge' : 'Google Chrome'} ${ver}`],
    ['ОС', os],
    ['Часовой пояс', f.timezone.mode === 'auto' ? 'по гео прокси' : f.timezone.value || '—'],
    ['Язык', f.language.mode === 'auto' ? 'по гео прокси' : f.language.value || '—'],
    ['Шум', f.noise ? 'включён' : 'выключен'],
    ['Canvas', !f.noise || f.canvas === 'real' ? 'реальный' : 'шум (уникальный на профиль)'],
    [
      'WebGL / GPU',
      f.webgl === 'real'
        ? 'реальный, без подмены'
        : f.platform === 'android'
          ? 'мобильный по seed (Adreno / Mali)'
          : f.hardware.source === 'library'
            ? 'GPU донора'
            : 'одинаковый у всех (Intel UHD 770 по умолчанию сборки)',
    ],
    ['Железо', hw],
    ['WebRTC', webrtc[f.webrtc] ?? f.webrtc],
    ['Порты', f.blockedPorts.length ? f.blockedPorts.join(', ') : 'не блокируются'],
  ];
}




export function resultCell(text: string | undefined, site: string): string {
  const t = String(text ?? '—');
  if (site === 'iphey' && t.includes('crash')) return 'не открылся (вкладка упала)';
  if (site === 'clearcote-audit') {
    const [head, ...findings] = t.split(' | ');
    return findings.length ? `${head} · замечаний: ${findings.length}` : (head ?? t);
  }
  return t.length > 120 ? `${t.slice(0, 117)}…` : t;
}

export function resultClass(text: string | undefined, site: string): string {
  const t = String(text || '');
  if (site === 'iphey' && t.includes('crash')) return '';
  if (!t || t.startsWith('?') || t.startsWith('ERR') || t.includes('crash')) return 'bad';
  if (site === 'pixelscan') return /^consistent/.test(t) && !t.includes('masking') ? 'ok' : 'bad';
  if (site === 'iphey') return /Trustworthy/i.test(t) ? 'ok' : 'bad';
  if (site === 'browserscan') return /^100%/.test(t) ? 'ok' : '';
  if (site === 'creepjs') {
    if (t.includes('не измерено') || t.includes('!!')) return 'bad';
    return /headless=0% stealth=0%/.test(t) ? 'ok' : '';
  }
  if (site === 'clearcote-audit') return /^(9\d|100)\//.test(t) ? 'ok' : '';
  return '';
}

export type FpReport = {
  tag: string;
  error?: string;
  persona?: { platform?: string; brand?: string; brandVersion?: string; seed?: string; donorId?: string };
  exitIp?: string;
  country?: string;
  timezone?: string;
  ipConsistent?: boolean;
  ipSeen?: string[];
  rotating?: boolean;
  navigator?: {
    platform?: string;
    cores?: number;
    memory?: number | null;
    screen?: string;
    tz?: string;
    langs?: string[];
    gpu?: string;
    webdriver?: boolean;
  };
  results?: Record<string, string | undefined>;
  shots?: string[];
};

export type FpRun = {
  id: string;
  status: string;
  source?: string;
  profileId?: string;
  count?: number;
  startedAt?: string;
  progress?: string[];
  error?: string;
  reports?: FpReport[];
};



export type DeletePreview = {
  count: number;
  matched: number;
  byPhase?: Record<string, number>;
  sample?: string[];
  skippedRunning?: number;
};

export type DeleteJob = {
  status: string;
  deleted: number;
  total: number;
  failed?: number;
  skippedRunning?: number;
};

export type DeleteRow = { preview?: DeletePreview; job?: DeleteJob; error?: string };



export type Persona = {
  seed?: string;
  platform?: string;
  brand?: string;
  brandVersion?: string;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  timezone?: string;
  language?: string;
  noise?: boolean;
  canvasNoise?: boolean;
  realGpu?: boolean;
  webrtc?: string;
  blockedPorts?: number[];
  createdAt?: string;
};

export function personaRows(
  persona: Persona | null | undefined,
  donor: Donor | null | undefined,
  engineChromium: number,
): Array<[string, string]> {
  if (!persona) return [['Отпечаток', 'ещё не создан (профиль не запускался)']];
  const wr: Record<string, string> = { proxy: 'IP прокси', disable_udp: 'non-proxied UDP выключен', default: 'по умолчанию' };
  return [
    ['Seed', persona.seed ?? '—'],
    ['ОС', persona.platform ?? '—'],
    ['Браузер', `${persona.brand || 'Chrome'} ${persona.brandVersion || `${engineChromium} (движок)`}`],
    [
      'Железо',
      donor
        ? `донор «${donor.name}» · ${donor.gpu || ''}`
        : `${persona.hardwareConcurrency ? `${persona.hardwareConcurrency} CPU` : 'CPU по seed'} · ${
            persona.deviceMemory ? `${persona.deviceMemory} ГБ` : 'RAM по seed'
          }`,
    ],
    ['Часовой пояс', persona.timezone || 'по гео прокси'],
    ['Язык', persona.language || 'по гео прокси'],
    [
      'Шум',
      persona.noise === false ? 'выключен' : persona.canvasNoise === false ? 'включён, canvas реальный' : 'включён',
    ],
    [
      'GPU',
      persona.realGpu
        ? 'реальный, без подмены'
        : donor
          ? 'донора'
          : 'Intel UHD 770 по умолчанию сборки (общий для всех без донора)',
    ],
    ['WebRTC', wr[persona.webrtc || 'proxy'] ?? '—'],
    ['Порты', persona.blockedPorts?.length ? persona.blockedPorts.join(', ') : '—'],
    ['Создан', persona.createdAt ? `${fmtDate(persona.createdAt)} UTC` : '—'],
  ];
}
